/**
 * @module components/Icons
 * @description Inline SVG icon set. Strokes use `currentColor`, so icons take
 * their color from the surrounding text token (no hard-coded colors).
 */
import type { JSX } from "react";

/** Every icon id. */
export type IconName =
    | "cart" | "heart" | "heart-filled" | "share" | "search" | "eye" | "chevron-left" | "chevron-right"
    | "close" | "grid" | "list" | "lock" | "receipt" | "shield" | "report" | "check" | "pause" | "play"
    | "arrow-right" | "sparkle";

/** SVG path data per icon (24×24 grid, stroked). */
const PATHS: Readonly<Record<IconName, string>> = {
    cart: "M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 1.9-1.4L21 8H6.2M10 20.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm8 0a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z",
    heart: "M12 20.5s-7.4-4.5-9.4-9C1.1 8 3.5 4.2 7.2 4.2c2.1 0 3.7 1.1 4.8 2.8 1.1-1.7 2.7-2.8 4.8-2.8 3.7 0 6.1 3.8 4.6 7.3-2 4.5-9.4 9-9.4 9Z",
    "heart-filled": "M12 20.5s-7.4-4.5-9.4-9C1.1 8 3.5 4.2 7.2 4.2c2.1 0 3.7 1.1 4.8 2.8 1.1-1.7 2.7-2.8 4.8-2.8 3.7 0 6.1 3.8 4.6 7.3-2 4.5-9.4 9-9.4 9Z",
    share: "M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M12 3v12M7.5 7.5 12 3l4.5 4.5",
    search: "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm9 3-4.3-4.3",
    eye: "M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
    "chevron-left": "M15 18 9 12l6-6",
    "chevron-right": "m9 18 6-6-6-6",
    close: "M18 6 6 18M6 6l12 12",
    grid: "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
    list: "M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01",
    lock: "M6 11h12v10H6zM8 11V7a4 4 0 0 1 8 0v4",
    receipt: "M6 3h12v18l-3-2-3 2-3-2-3 2V3Zm3 5h6M9 12h6M9 16h3",
    shield: "M12 3 4 6v6c0 5 3.4 8.3 8 9 4.6-.7 8-4 8-9V6l-8-3Zm-3.5 9 2.5 2.5 4.5-5",
    report: "M5 21V9m7 12V3m7 18v-7",
    check: "m5 12.5 4.5 4.5L19 7.5",
    pause: "M8 5v14M16 5v14",
    play: "M7 4.5v15l12-7.5-12-7.5Z",
    "arrow-right": "M5 12h14m-6-6 6 6-6 6",
    sparkle: "M12 3v4m0 10v4M3 12h4m10 0h4M6 6l2.5 2.5m7 7L18 18M6 18l2.5-2.5m7-7L18 6",
};

/** Icons drawn filled rather than stroked. */
const FILLED: ReadonlySet<IconName> = new Set<IconName>(["heart-filled", "play"]);

/** Props for <Icon>. */
interface IconProps {
    name: IconName;
    /** Pixel size (square). Default 20. */
    size?: number;
    /** Accessible label; omit for decorative icons (then aria-hidden). */
    label?: string;
}

/** Renders one icon. Decorative by default. */
export function Icon({ name, size = 20, label }: IconProps): JSX.Element {
    const filled: boolean = FILLED.has(name);
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill={filled ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            role={label ? "img" : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
            focusable="false"
        >
            <path d={PATHS[name]} />
        </svg>
    );
}
